/**
 * ID stabili dei job della vista /jobs, usati come ancora del deep link `/jobs#<id>`.
 *
 * Un job è un deployment Prefect: l'ID è lo slug del nome del deployment (es.
 * `findings-snapshot`), uguale a ogni reload e su ogni istanza (il deployment id UUID cambia da
 * un ambiente all'altro, il nome no). Il run id non va bene: cambia a ogni esecuzione.
 */

import type { JobRun } from './types.js';

/** Slug URL-safe: minuscolo, solo [a-z0-9-], senza trattini ai bordi. */
export function slugify(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** ID "naturale" di un job, prima della disambiguazione. */
function baseJobId(run: JobRun): string {
  const name = run.deploymentName ?? (run.deploymentId ? run.flowName : undefined);
  const slug = name ? slugify(name) : '';
  // Run senza deployment (avviato a mano fuori da un deployment): la riga è quel run.
  return slug || `run-${slugify(run.id)}`;
}

/**
 * Mappa run id → job id univoco nella lista. Due deployment con lo stesso nome (possibile in
 * Prefect se appartengono a flow diversi) ricevono un suffisso dal proprio deployment id, che
 * resta stabile finché il deployment esiste.
 */
export function jobIdsFor(runs: readonly JobRun[]): Map<string, string> {
  const counts = new Map<string, number>();
  for (const run of runs) {
    const id = baseJobId(run);
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  const out = new Map<string, string>();
  for (const run of runs) {
    const id = baseJobId(run);
    const duplicate = (counts.get(id) ?? 0) > 1 && run.deploymentId;
    out.set(run.id, duplicate ? `${id}-${slugify(run.deploymentId!).slice(0, 8)}` : id);
  }
  return out;
}

/** Job id dall'hash della URL (`#findings-snapshot` → `findings-snapshot`), o undefined. */
export function jobIdFromHash(hash: string): string | undefined {
  const raw = hash.replace(/^#/, '');
  if (!raw) return undefined;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}
